import React from 'react';
import { motion } from 'framer-motion';
import { MapPin, GraduationCap, Award } from 'lucide-react';

/**
 * SEO-friendly section that positions Whiteplains British School alongside
 * commonly-searched schools in Abuja and Nigeria. This helps Google understand
 * the local school landscape and rank Whiteplains for relevant comparison queries.
 */
const peerSchools = [
  'Lead British Academy',
  'The Regent School',
  'Premier International School',
  'Capital Science Academy',
  'Start-Rite Schools',
  'FUNTAJ International School',
];

const SchoolsInAbuja = () => {
  return (
    <section
      id="schools-in-abuja"
      className="relative py-20 md:py-28 bg-[#fbf7f2] border-t border-[#eadfc9]"
      aria-labelledby="peers-heading"
    >
      <div className="max-w-7xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="grid md:grid-cols-12 gap-10 md:gap-16 items-start"
        >
          <div className="md:col-span-5">
            <div className="tag-label">Among the top schools in Abuja</div>
            <h2
              id="peers-heading"
              className="font-serif-display text-3xl md:text-5xl font-black text-[#1c1a17] leading-[1.05] mt-4"
            >
              A leading international school in <span className="italic text-[#b8331f]">Abuja, Nigeria &amp; Africa</span>
            </h2>
            <div className="h-px w-16 bg-[#c8942a] my-6" />
            <p className="text-[#4a4238] leading-[1.75] text-[15px] font-light">
              <strong>Whiteplains British School (WBS)</strong> is one of the most respected
              Cambridge Pathway international schools in Abuja and across Nigeria. Families comparing
              the best schools in Abuja — alongside institutions such as those listed here — choose
              Whiteplains for its rigorous British curriculum, outstanding IGCSE and A-Level results,
              and its unique VR learning, AI teacher and student microfinance programmes.
            </p>
            <div className="mt-6 space-y-3 text-[13px] text-[#4a4238]">
              <div className="flex items-center gap-3">
                <MapPin size={16} className="text-[#c8942a]" /> Plot 528, Cadastral Zone B4, opposite Ukrainian Embassy, Abuja
              </div>
              <div className="flex items-center gap-3">
                <GraduationCap size={16} className="text-[#c8942a]" /> Cambridge Pathway — Pre-Nursery to A-Level
              </div>
              <div className="flex items-center gap-3">
                <Award size={16} className="text-[#c8942a]" /> IGCSE • GCSE • A-Level • Cambridge Checkpoint
              </div>
            </div>
          </div>

          <div className="md:col-span-7">
            <div className="font-sans-pro text-[11px] uppercase tracking-[0.4em] text-[#8f6417] font-semibold">
              Comparable schools parents often research
            </div>
            <ul className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
              {peerSchools.map((name) => (
                <li
                  key={name}
                  className="flex items-center gap-3 bg-white border border-[#eadfc9] px-4 py-3 text-[#1c1a17] font-serif-display text-[15px]"
                >
                  <span className="w-2 h-2 rounded-full bg-[#c8942a] shrink-0" />
                  {name}
                </li>
              ))}
            </ul>
            <p className="mt-6 text-[12px] text-[#8f6417]">
              Whiteplains British School is featured among leading Nigerian British schools. All
              other names above belong to their respective owners and are shown for parental
              reference only.
            </p>
          </div>
        </motion.div>

        {/* Hidden but crawlable semantic content — for local SEO */}
        <div className="sr-only" aria-hidden="false">
          <h3>Whiteplains British School — Schools in Abuja &amp; Nigeria</h3>
          <p>
            Whiteplains British School (WBS) is a leading Cambridge Pathway international school in Abuja,
            Nigeria, serving families across the FCT and West Africa. As one of the top British curriculum
            schools in Abuja, Whiteplains offers Pre-Nursery, Nursery, Primary, Secondary, IGCSE and A-Level
            programmes with a strong focus on academic excellence and character development.
          </p>
          <p>
            Parents comparing schools in Abuja often research Whiteplains British School alongside Lead British
            Academy, The Regent School, Premier International School, Capital Science Academy, Start-Rite Schools,
            and FUNTAJ International School. Whiteplains distinguishes itself with immersive VR learning, an AI
            lecturer (Mr Johnson Okeke), and an on-campus microfinance bank that teaches financial literacy from
            an early age.
          </p>
          <p>
            Keywords: Whiteplains British School, WBS, Whiteplains Abuja, Schools in Abuja, Best schools in Abuja,
            International schools in Abuja, Schools in Nigeria, Schools in Africa, British curriculum school
            Nigeria, Cambridge Pathway school Abuja, IGCSE Abuja, A-Level Abuja.
          </p>
        </div>
      </div>
    </section>
  );
};

export default SchoolsInAbuja;
